import React, { useState, useRef, useEffect } from "react"
import { Offcanvas, CloseButton, Button, Form, Modal, Tab, Nav, Card, Row, Col } from 'react-bootstrap'
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, getDoc, deleteDoc, query, where } from 'firebase/firestore'
import { FaCrown, FaUserTag, FaEnvelope, FaCalendarAlt, FaTrashAlt, FaUser, FaMapMarkerAlt, FaLock, FaHistory, FaUsers, FaShieldAlt, FaCheckCircle, FaCheck, FaTimesCircle, FaFlag, FaExclamationTriangle, FaCamera, FaCameraRetro, FaQrcode, FaPrint } from 'react-icons/fa';
import { Scanner } from '@yudiel/react-qr-scanner';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify'
import { convertDate, spinner } from "../utils/Helper"
import ChangePasswordButton from "../entry/ChangePasswordButton";
import apc from '../images/user.png'
import MemberTransactionHistory from "./MemberTransactionHistory";
import ManualPayment from "./ManualPayment";

import './xDocList.css';

// --- SUB-COMPONENT: UPDATE PROFILE ---
export function UpdateProfile({ idx, filex, name, type, theme, onSendToParent, hide, isTriggerOnly = false, smallIcon }) {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const fileInputRef = useRef(null);
    const [show, setShow] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [historyLogs, setHistoryLogs] = useState([]);
    const [membershipRules, setMembershipRules] = useState({});

    const [formData, setFormData] = useState({
        name: '', dob: '', details: '', filex: '', type: '', email: '', expirationDate: '', id: '', board: false, cell: '',
        title: '', member: false, otherPhone: '', address1: '', address2: '', city: '',
        state: '', zip: '', country: 'USA', company: '', jobTitle: '', paidThrough: '', serverPermit: '',
        sponsor: '', isAdmin: false, isBarAdmin: false,
    });

    const fetchMembershipRules = async () => {
        const snap = await getDocs(collection(db, "membership_levels"));
        const rules = {};
        snap.forEach(doc => { rules[doc.id] = doc.data(); });
        setMembershipRules(rules);
    };

    const fetchHistory = async (id) => {
        const historyRef = collection(db, "profiles", id, "history");
        const snap = await getDocs(historyRef);
        const logs = snap.docs.map(d => d.data());
        setHistoryLogs(logs.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
    };

    const handleOpen = async () => {
        if (!idx) return;
        spinner(true);
        try {
            await fetchMembershipRules();
            const docSnap = await getDoc(doc(db, "profiles", idx));
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData({ ...data });
                setImagePreview(data.filex || apc);
                setActiveTab('profile');
                setShow(true);
                fetchHistory(idx);
            }
        } catch (err) { toast.error("Load failed"); }
        finally { spinner(false); }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const emailToStore = formData.email.toLowerCase().trim();

        spinner(true);
        try {
            const docRef = doc(db, "profiles", formData.id);
            const docSnap = await getDoc(docRef);
            const oldData = docSnap.data();

            if (emailToStore !== oldData.email?.toLowerCase()) {
                const q = query(collection(db, "profiles"), where("email", "==", emailToStore));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    spinner(false);
                    return toast.error("This email is already in use by another member.");
                }
            }

            await updateDoc(docRef, {
                ...formData,
                email: emailToStore,
                filex: imagePreview === apc ? '' : imagePreview
            });
            toast.success("Updated!");
            setShow(false);
            if (onSendToParent) onSendToParent(true);
        } catch (err) { toast.error(err.message); }
        finally { spinner(false); }
    };

    const renderFeeDisplay = () => {
        if (!formData.member || !formData.type || !membershipRules[formData.type]) return null;
        const info = membershipRules[formData.type];
        return (
            <div className={`mt-3 p-2 rounded border shadow-sm ${isDark ? 'bg-info bg-opacity-10 border-info text-info' : 'bg-info bg-opacity-10 border-info text-info'}`}>
                <div className="d-flex align-items-center justify-content-center gap-2 small">
                    <span className="fw-bold text-info">Membership Fee</span>
                    <span className="badge bg-info text-dark ms-2">${info.cost} {info.occurrence}</span>
                </div>
            </div>
        );
    };

    const renderStatusBanner = () => {
        if (!hide) return null;
        const levelData = membershipRules[formData.type] || {};
        return (
            <div className={` ${isDark ? '' : ''}`}>
                <div className="d-flex flex-wrap justify-content-center justify-content-md-start" style={{ gap: '8px' }}>
                    {formData.board && (
                        <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                            <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Board</label>
                            <div className="small fw-bold text-warning d-flex align-items-center">
                                <FaCrown className="me-1" /> {formData.title}
                            </div>
                        </div>
                    )}

                    <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                        <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Member</label>
                        <div className={`small fw-bold ${!isDark ? 'text-dark' : 'text-white'}`}>
                            {formData.member ? formData.type : 'Non-Member'}
                        </div>
                    </div>

                    <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                        <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Expires</label>
                        <div className="small fw-bold text-danger d-flex align-items-center">
                            <FaCalendarAlt className="me-1" /> {formData.expirationDate ? convertDate(formData.expirationDate) : 'N/A'}
                        </div>
                    </div>

                    {levelData.account_flag && (
                        <div className={`px-3 py-2 rounded-pill border ${isDark ? 'bg-dark border-secondary' : 'bg-white border-secondary'}`} style={{ flex: '0 1 auto' }}>
                            <label className={`meta-label d-block mb-0 opacity-50 ${!isDark ? 'text-dark' : 'text-white'}`} style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase' }}>Status</label>
                            <div className="small fw-bold text-info d-flex align-items-center">
                                <FaFlag className="me-1" /> Flagged
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const modalStyles = {
        backdrop: isDark ? "adminBackdrop" : "adminBackdropWhite",
        content: isDark ? "bg-dark text-white shadow-lg" : "bg-light text-dark shadow-lg",
        input: isDark ? "glass-input" : "form-control border-light-subtle mb-3",
        nav: isDark ? 'profile-nav-dark' : 'profile-nav-light'
    };

    return (
        <>
            {!isTriggerOnly && !hide ? (
                <Button
                    onClick={handleOpen}
                    className={
                        smallIcon
                            ? "btn-sm p-0"
                            : "circle-btn centerCircle text-danger shadow-sm"
                    }
                >
                    👤
                </Button>
            ) : (
                <Button onClick={handleOpen} >
                    <img src={filex || apc} alt="User" className="thumb hoverX animated fadeInRight mx-auto border border-3 orange rounded-circle overflow-hidden" />
                    <span className="d-block">{name || "Profile"}</span>
                </Button>
            )}

            <Modal show={show} onHide={() => setShow(false)} centered size="lg" contentClassName={modalStyles.content} backdropClassName={modalStyles.backdrop}>
                <Modal.Header className={`border-bottom border-opacity-10 px-4 ${!isDark ? 'text-dark' : ''}`}>
                    <Modal.Title className="d-flex align-items-center gap-3">
                        <QRCode value={formData.email || "none"} size={45} />
                        <div><h4 className="m-0">{formData.name}</h4><div className="small opacity-50">ID: {formData.id}</div></div>
                    </Modal.Title>
                    <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShow(false)} />
                </Modal.Header>
                <Modal.Body className="p-0">

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <div className="px-4 pt-3 border-bottom border-opacity-10">
                            <Nav variant="pills" className={`flex-column flex-md-row gap-2 mb-3 ${modalStyles.nav}`}>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="profile" className="text-center py-2"><FaUser className="me-2" /> General</Nav.Link></Nav.Item>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="address" className="text-center py-2"><FaMapMarkerAlt className="me-2" /> Address</Nav.Link></Nav.Item>
                                {!hide && (
                                    <Nav.Item className="flex-fill"><Nav.Link eventKey="staff" className="text-center py-2"><FaLock className="me-2" /> Staff Only</Nav.Link></Nav.Item>
                                )}
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="history" className="text-center py-2"><FaHistory className="me-2" /> History</Nav.Link></Nav.Item>
                            </Nav>
                        </div>
                        <Tab.Content>
                            <Tab.Pane eventKey="profile">
                                <div className="p-4"><div className="row">
                                    <div className="col-md-4 text-center border-md-end border-opacity-10">
                                        <div className="mx-auto mb-3 rounded-circle overflow-hidden shadow" style={{ width: '150px', height: '150px', border: '3px solid #ff8c00' }}><img src={imagePreview || apc} className="w-100 h-100" style={{ objectFit: 'cover' }} /></div>
                                        <div className="d-grid gap-2">
                                            <Button size="sm" variant="outline-info" onClick={() => fileInputRef.current.click()}>Change Photo</Button>
                                            <ChangePasswordButton theme={theme} />
                                        </div>
                                        <input type="file" className="d-none" ref={fileInputRef} onChange={(e) => {
                                            const reader = new FileReader();
                                            reader.onload = () => setImagePreview(reader.result);
                                            reader.readAsDataURL(e.target.files[0]);
                                        }} />
                                        {renderFeeDisplay()}
                                    </div>
                                    <div className="col-md-8 ps-md-4">
                                        <label className="meta-label">Full Name</label><input type="text" className={modalStyles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                        <label className="meta-label">Email Address (Primary Login ID)</label><input type="email" className={modalStyles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} readOnly={hide} />
                                        <div className="row">
                                            <div className="col-6"><label className="meta-label">Cell Phone</label><input type="text" className={modalStyles.input} placeholder="123-456-7890" value={formData.cell} onChange={(e) => setFormData({ ...formData, cell: e.target.value })} /></div>
                                            <div className="col-6"><label className="meta-label">Date of Birth</label><input type="date" className={modalStyles.input} value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} /></div>
                                        </div>
                                        <label className="meta-label mt-2">Bio / Details</label><textarea className={modalStyles.input} rows="3" value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} />
                                    </div>
                                </div>
                                    {renderStatusBanner()}
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="address">
                                <div className="p-4">
                                    <label className="meta-label">Street Address 1</label><input type="text" className={modalStyles.input} value={formData.address1} onChange={(e) => setFormData({ ...formData, address1: e.target.value })} />
                                    <label className="meta-label">Street Address 2</label><input type="text" className={modalStyles.input} value={formData.address2} onChange={(e) => setFormData({ ...formData, address2: e.target.value })} />
                                    <div className="row g-2">
                                        <div className="col-md-4"><label className="meta-label">City</label><input type="text" className={modalStyles.input} value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                                        <div className="col-md-4"><label className="meta-label">State</label><input type="text" className={modalStyles.input} value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} /></div>
                                        <div className="col-md-4"><label className="meta-label">Zip</label><input type="text" className={modalStyles.input} value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} /></div>
                                    </div>
                                    <hr className="my-3 opacity-10" />
                                    <div className="row">
                                        <div className="col-md-6"><label className="meta-label">Company / Employer</label><input type="text" className={modalStyles.input} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} /></div>
                                        <div className="col-md-6"><label className="meta-label">Job Title</label><input type="text" className={modalStyles.input} value={formData.jobTitle} onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })} /></div>
                                    </div>
                                </div>
                            </Tab.Pane>
                            {!hide && (
                                <Tab.Pane eventKey="staff">
                                    <div className="p-4">
                                        <div className={`p-4 rounded border ${isDark ? 'bg-secondary bg-opacity-10 border-info' : 'bg-light border-info'}`}>

                                            {/* SYSTEM PERMISSIONS SECTION */}
                                            <div className="mb-4 border-bottom border-info border-opacity-10 pb-3">
                                                <label className="meta-label text-info d-flex align-items-center gap-2 mb-2">
                                                    <FaShieldAlt /> System Permissions
                                                </label>
                                                <Form.Check
                                                    type="switch"
                                                    id="admin-switch"
                                                    label="Admin Access (Dashboard, Directory - Master User - Unlimited Access)"
                                                    checked={formData.isAdmin}
                                                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                                                    className="fw-bold text-danger"
                                                />

                                                   <Form.Check
                                                    type="switch"
                                                    id="admin-switch"
                                                    label="Bar Admin Access (Scanner, Incident Reporting & Manual Payment Recording)"
                                                    checked={formData.isBarAdmin}
                                                    onChange={(e) => setFormData({ ...formData, isBarAdmin: e.target.checked })}
                                                    className="fw-bold text-danger"
                                                />
                                            </div>

                                            <div className="row g-3 mb-4">
                                                <div className="col-md-6"><label className="meta-label text-info">Membership Expires</label><input type="date" className={modalStyles.input} value={formData.expirationDate} onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })} /></div>
                                                <div className="col-md-6"><label className="meta-label text-info">Paid Through</label><input type="date" className={modalStyles.input} value={formData.paidThrough} onChange={(e) => setFormData({ ...formData, paidThrough: e.target.value })} /></div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <Form.Check type="switch" label="Board Member" checked={formData.board} onChange={(e) => setFormData({ ...formData, board: e.target.checked })} />
                                                    {formData.board && (
                                                        <Form.Select className={modalStyles.input + " mt-2"} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}>
                                                            <option value="">-- Choose Title --</option>
                                                            <option value="President">President</option>
                                                            <option value="Vice-President">Vice-President</option>
                                                            <option value="Secretary">Secretary</option>
                                                            <option value="Treasurer">Treasurer</option>
                                                            <option value="Director">Director</option>
                                                        </Form.Select>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="meta-label text-info">Server Permit #</label>
                                                    <input type="text" className={modalStyles.input} value={formData.serverPermit} onChange={(e) => setFormData({ ...formData, serverPermit: e.target.value })} />
                                                </div>
                                            </div>
                                            <Form.Check type="switch" label="Club Member" checked={formData.member} onChange={(e) => setFormData({ ...formData, member: e.target.checked })} />
                                            {formData.member && (
                                                <div className="mt-2 ms-4 border-start ps-3 border-info border-opacity-25">
                                                    <Form.Select className={modalStyles.input} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                                        <option value="">-- Type --</option>
                                                        {Object.keys(membershipRules).map(t => <option key={t} value={t}>{t}</option>)}
                                                    </Form.Select>
                                                    <label className="meta-label mt-2">Sponsor</label>
                                                    <input type="text" className={modalStyles.input} value={formData.sponsor} onChange={(e) => setFormData({ ...formData, sponsor: e.target.value })} />
                                                    {renderFeeDisplay()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab.Pane>
                            )}
                            <Tab.Pane eventKey="history">
                                <div className="p-4 custom-scrollbar" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {historyLogs.map((log, i) => (
                                        <div key={i} className={`mb-2 p-3 rounded border ${isDark ? ' bg-opacity-5' : 'bg-secondary bg-opacity-10 shadow-sm'}`}>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-info fw-bold">{log.updatedBy}</span>
                                                <span className="opacity-50 text-end">{log.timestamp?.toDate ? convertDate(log.timestamp.toDate()) : 'Recent'}</span>
                                            </div>
                                            <div className="small opacity-75">{log.changes}</div>
                                        </div>
                                    ))}
                                </div>

                                <MemberTransactionHistory memberEmail={formData.email} />

                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                    <div className="p-4 border-top border-opacity-10 d-flex gap-2">
                        <Button variant="secondary" className="px-4" onClick={() => setShow(false)}>Cancel</Button>
                        <Button onClick={handleUpdate} variant="info" className="flex-grow-1 text-white fw-bold">Save All Changes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <style>{`
                .meta-label { font-size: 10px; text-transform: uppercase; opacity: 0.6; display: block; margin-bottom: 2px; }
                .bg-black { background-color: #070707 !important; border: 1px solid #77777757;}
            `}</style>
        </>
    );
}

// --- MEMBER LIST ITEM ---
const XList = (props) => {
    const isDark = props.theme === 'dark';
    const db = getFirestore();
    const [membershipRules, setMembershipRules] = useState({});

    useEffect(() => {
        getDocs(collection(db, "membership_levels")).then(snap => {
            const rules = {};
            snap.forEach(doc => rules[doc.id] = doc.data());
            setMembershipRules(rules);
        });
    }, [db]);

    const handleApprove = async () => {
        if (window.confirm(`Approval ${props.name}?`)) {
            spinner(true);
            try {
                await updateDoc(doc(db, "profiles", props.id), { member: true });
                toast.success("Approved.");
                props.sendToXMembersLight(true);
            } catch (err) { console.error("Error Approving:", err); }
        }
    };

    const handleReportIncident = () => {
        toast.info(`Incident reporting placeholder for ${props.name}`);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    const expDate = props.expirationDate ? new Date(props.expirationDate) : null;
    let isExpired = false;
    let daysLeft = null;

    if (expDate) {
        expDate.setHours(0, 0, 0, 0);
        isExpired = expDate <= today;

        const diffTime = expDate - today;
        daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    const hasAccountFlag = membershipRules[props.type]?.account_flag;

    return (
        <div className="doc-card animated fadeIn mb-1 p-0 overflow-hidden position-relative"
            style={{ border: isDark ? '1px solid rgba(36, 29, 59, 0.15)' : '1px solid #2e224a', background: isDark ? 'rgba(6, 6, 6, 0.03)' : '#fff' }}>

            <div className="p-1">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="text-start flex-grow-1">
                        <h5 className="m-0 fw-bold" style={{ color: isDark ? '#00d4ff' : '#0056b3' }}>{props.name} </h5>
                        <div className={isDark ? "text-white-50" : "text-muted"} style={{ fontSize: '11px', marginTop: '10px' }}><FaEnvelope className="me-2 text-info" /> {props.email}</div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {props.board && <span className="text-warning small d-flex align-items-center"><FaCrown className="me-1" /> {props.title}</span>}
                            <span className={isDark ? "text-white-50 small" : "text-muted small"}><FaUserTag className="me-1" /> {props.type}</span>
                            {props.isAdmin && <span className="text-danger small d-flex align-items-center fw-bold"><FaShieldAlt className="me-1" /> Admin</span>}
                            {props.isBarAdmin && <span className="text-info small d-flex align-items-center fw-bold"><FaShieldAlt className="me-1" /> Bar Admin</span>}
                            {hasAccountFlag && <span className="text-info small d-flex align-items-center fw-bold"><FaFlag className="me-1" /> Flagged</span>}
                            
                        </div>
                    </div>
                </div>
                <div className="mt-3 d-flex align-items-center justify-content-between">
                    <div>
                        {isExpired ? (
                            <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger border px-2" style={{ fontSize: '10px' }}>EXPIRED</span>
                        ) : (
                            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border px-2" style={{ fontSize: '10px' }}>ACTIVE</span>
                        )}
                        <span className={isDark ? "ms-2 text-white-50 small" : "ms-2 text-muted small"}>
                            <FaCalendarAlt className="me-1" size={10} />
                            {props.expirationDate ? convertDate(props.expirationDate) : 'No Date'}
                        </span>

                        {!isExpired && daysLeft !== null && daysLeft <= 40 && (
                            <span className="ms-2 text-warning fw-bold" style={{ fontSize: '11px' }}>
                                ({daysLeft} days remaining)
                            </span>
                        )}

                        {props.hasIncident && (
                            <span className="ms-2 text-warning fw-bold" style={{ fontSize: '11px' }}>
                               Has Incident
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="d-flex justify-content-end border-top border-opacity-10 border-secondary p-1 m-1">
                <div className="btn-group p-1" role="group">
                    {!props.member && props.membershipStatus == 'active' ? <Button onClick={handleApprove} className="circle-btn centerCircle needs-approval" ><FaCheck className="orange" size={10} /></Button> : ''}&nbsp;
                    
                    <Button onClick={handleReportIncident} className="circle-btn centerCircle shadow-sm text-warning" title="Report Incident">
                        <FaExclamationTriangle size={10} />
                    </Button>&nbsp;

                    <ManualPayment
                        profileId={props.id}
                        userEmail={props.email}
                        userName={props.name}
                        typeOfMembership={props.type} 
                        expirationDate={props.expirationDate} 
                        theme="dark"
                        onSendToParent={() => props.sendToXMembersLight(true)}
                    />
                </div>
            </div>
        </div>
    );
};

// --- MAIN EXPORT ---
function XMembersLight(props) {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showCamera, setShowCamera] = useState(false);
    const [activeTab, setActiveTab] = useState("members");
    
    // Hardware scan key handlers
    const bufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);

    const getMembers = () => {
        spinner(true);
        getDocs(collection(getFirestore(), "profiles")).then((snapshot) => {
            let rec = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setData(rec);
            spinner(false);
        }).catch(() => spinner(false));
    };

    // Load data once when offcanvas is opened
    useEffect(() => {
        if (props.isOpen) {
            getMembers();
        }
    }, [props.isOpen]);

    // Phone / Native Camera Scan Callback
    const handleCameraScan = (result) => {
        if (result && result.length > 0) {
            const rawValue = result[0].rawValue;
            setSearchTerm(rawValue);
            setShowCamera(false); // Auto-close camera view container on success
            toast.success("Card Scanned via Camera");
        }
    };

    // Global background hardware listener interceptor
    useEffect(() => {
        if (!props.isOpen) return;

        const handleKeyPress = (e) => {
            const currentTime = Date.now();
            if (currentTime - lastKeyTimeRef.current > 100) {
                bufferRef.current = '';
            }
            lastKeyTimeRef.current = currentTime;

            if (e.key === 'Enter') {
                if (bufferRef.current.length > 2) {
                    const scannedValue = bufferRef.current.trim();
                    setSearchTerm(scannedValue);
                    bufferRef.current = '';
                    toast.success("Card Scanned via Barcode Gun");
                }
            } else {
                if (e.key !== 'Shift') {
                    bufferRef.current += e.key;
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [props.isOpen]);

    // Reusable filter helper
    const filteredMembers = data.filter((m) => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;

        const matchName = m.name?.toLowerCase().includes(term);
        const matchEmail = m.email?.toLowerCase().includes(term);
        const matchType = m.type?.toLowerCase().includes(term);

        const matchAdmin = term === 'admin' && m.isAdmin;
        const matchBarAdmin = term === 'bar admin' && m.isBarAdmin;
        const matchBoard = term === 'board member' && m.board;

        let matchExpired = false;
        if (term === 'expired' && m.expirationDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const expDate = new Date(m.expirationDate);
            expDate.setHours(0, 0, 0, 0);
            matchExpired = expDate <= today;
        }

        return matchName || matchEmail || matchType || matchAdmin || matchBarAdmin || matchBoard || matchExpired;
    });

    const handlePrint = () => {
        window.print();
    };

    const handleOpenClick = () => {
        if (props.onToggle) {
            props.onToggle();
        }
    };

    return (
        <>
        <Button variant={isDark ? "primary" : "outline-primary"} onClick={handleOpenClick} className="hoverX shadow-sm"><span className="circle-btn centerCircle">💛</span> Directory</Button>
            <Offcanvas show={props.isOpen} onHide={() => { if (props.onToggle) props.onToggle(); setSearchTerm(""); setShowCamera(false); }} placement="end" backdropClassName="adminBackdrop" className={isDark ? "bg-dark" : "bg-light border-start"}>
                <Offcanvas.Header className="pb-3 border-bottom border-opacity-10 print-hide">
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className={isDark ? "modified xTitle m-0" : "text-dark fw-bold m-0"}>Directory</h3>
                            <CloseButton variant={isDark ? "white" : undefined} onClick={() => { if (props.onToggle) props.onToggle(); setSearchTerm(""); setShowCamera(false); }} />
                        </div>
                        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                            <Nav variant="pills" className="flex-row gap-2 mb-1 profile-nav-dark">
                                <Nav.Item className="flex-fill">
                                    <Nav.Link eventKey="members" className="text-center py-2 d-flex align-items-center justify-content-center gap-2">
                                        <FaUsers /> Members
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item className="flex-fill">
                                    <Nav.Link eventKey="qrcodes" className="text-center py-2 d-flex align-items-center justify-content-center gap-2 position-relative">
                                        <FaQrcode /> QR Codes
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Tab.Container>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="custom-scrollbar p-0">
                    <div className="p-3">
                        
                        {/* Interactive Search input layout with Phone Camera trigger button */}
                        <div className="d-flex gap-2 mb-3 print-hide">
                            <Form.Control 
                                type="search" 
                                placeholder="🔎 Search members or scan card..." 
                                className={isDark ? "glass-search m-0" : "form-control m-0"} 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                            />
                            <Button 
                                variant={showCamera ? "warning" : "info"} 
                                className="text-white d-flex align-items-center px-3"
                                onClick={() => setShowCamera(!showCamera)}
                                title="Toggle Phone Camera Scanner"
                            >
                                <FaCamera size={16} />
                            </Button>
                        </div>

                        {/* Native Phone Camera Scanner view port container */}
                        {showCamera && (
                            <div className="qr-video-container mb-3 border border-secondary rounded overflow-hidden shadow-sm pos-relative outer-scanner-frame print-hide">
                                <Scanner 
                                    onScan={handleCameraScan} 
                                    formats={['qr_code', 'pdf417']} 
                                    styles={{ container: { height: '100%', width: '100%' }, video: { objectFit: 'cover' } }}
                                />
                                <div className="custom-scanner-overlay">
                                    <div className="scanner-square-finder">
                                        <div className="corner-bracket tl"></div>
                                        <div className="corner-bracket tr"></div>
                                        <div className="corner-bracket bl"></div>
                                        <div className="corner-bracket br"></div>
                                        <div className="scanner-laser-line"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "members" ? (
                            <ul className="doc-container p-0">
                                {filteredMembers.map((member) => (
                                    <XList key={member.id} theme={theme} sendToXMembersLight={getMembers} {...member} hide={false} />
                                ))}
                            </ul>
                        ) : (
                            <div>
                                <div className="d-flex justify-content-end mb-3 print-hide">
                                    <Button variant="info" className="text-white fw-bold d-flex align-items-center gap-2 shadow-sm" onClick={handlePrint}>
                                        <FaPrint /> Print QR Codes
                                    </Button>
                                </div>
                                <div className="qr-print-grid">
                                    {filteredMembers.map((member) => (
                                        <div 
                                            key={member.id} 
                                            className="animated fadeIn mb-2 p-3 rounded text-start d-flex align-items-center justify-content-between qr-print-card"
                                            style={{ 
                                                border: isDark ? '1px solid rgba(77, 77, 77, 0.61)' : '1px solid #462a67', 
                                                background: isDark ? 'rgba(6, 6, 6, 0.2)' : '#fff' 
                                            }}
                                        >
                                            <div style={{ maxWidth: '60%' }}>
                                                <div className="apc padding animated rollIn modified-apc">APC</div>
                                                <div className="small text-muted mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Albuquerque Press Club</div>
                                                <h5 className="m-0 fw-bold text-truncate" style={{ color: isDark ? '#00d4ff' : '#0056b3' }}>{member.name}</h5>
                                                <div className={`text-truncate small mt-1 ${isDark ? "text-white-50" : "text-muted"}`}>
                                                    <FaEnvelope className="me-1 text-info" /> {member.email}
                                                </div>
                                            </div>
                                            <div className="p-2 bg-white rounded d-flex align-items-center justify-content-center border qr-wrapper">
                                                <QRCode value={member.email || "none"} size={145} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

        <style>{`
        .pos-relative { position: relative; }
        
        .qr-video-container {
            width: 100%;
            aspect-ratio: 1 / 1;
            max-height: 420px;
            background-color: #050505;
        }
        
        /* Strip library borders inside the bounding viewport wrapper safely */
        .qr-video-container div:not(.custom-scanner-overlay):not(.scanner-square-finder):not(.corner-bracket):not(.scanner-laser-line), 
        .qr-video-container svg {
            border: none !important;
            box-shadow: none !important;
        }
        
        .custom-scanner-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: radial-gradient(circle, rgba(0,0,0,0.1) 40%, rgba(220,53,69,0.12) 85%, rgba(0,0,0,0.65) 100%);
            pointer-events: none;
            z-index: 10;
        }
        .scanner-square-finder {
            position: relative;
            width: 72%;
            height: 72%;
            max-width: 280px;
            max-height: 280px;
            aspect-ratio: 1 / 1;
            background: transparent;
        }
        .corner-bracket {
            position: absolute;
            width: 24px;
            height: 24px;
            border: 4px dashed #ff3b30;
            filter: drop-shadow(0px 0px 6px rgba(255, 59, 48, 0.8));
        }
        .corner-bracket.tl { top: 0; left: 0; border-right: none; border-bottom: none; border-top-left-radius: 4px; }
        .corner-bracket.tr { top: 0; right: 0; border-left: none; border-bottom: none; border-top-right-radius: 4px; }
        .corner-bracket.bl { bottom: 0; left: 0; border-right: none; border-top: none; border-bottom-left-radius: 4px; }
        .corner-bracket.br { bottom: 0; right: 0; border-left: none; border-top: none; border-bottom-right-radius: 4px; }
        
        .scanner-laser-line {
            position: absolute;
            left: 4%;
            right: 4%;
            height: 3px;
            background: linear-gradient(90deg, transparent 5%, #ff3b30 50%, transparent 95%);
            box-shadow: 0 0 12px #ff30c1, 0 0 22px rgba(255, 48, 193, 0.86);
            opacity: 0.95;
            animation: scanMotion 2s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
        }
        @keyframes scanMotion {
            0% { top: 6%; transform: scaleX(0.96); opacity: 0.8; }
            50% { top: 92%; transform: scaleX(1.02); opacity: 1; }
            100% { top: 6%; transform: scaleX(0.96); opacity: 0.8; }
        }

        /* --- PRINT MEDIA QUERIES FOR PDF GENERATION --- */
        @media print {
            /* Hide the underlying root elements completely */
            #root, 
            #root > *, 
            main, 
            header, 
            footer, 
            .print-hide, 
            .offcanvas-backdrop {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
            }

            /* Clean global reset to default browser view styles */
            html, body {
                background: #ffffff !important;
                color: #000000 !important;
                height: auto !important;
                overflow: visible !important;
                margin: 0 !important;
                padding: 0 !important;
            }

            /* Override Bootstrap panel positions to expand full width directly on page */
            .offcanvas {
                display: block !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                width: 100% !important;
                height: auto !important;
                background: #ffffff !important;
                transform: none !important;
                box-shadow: none !important;
                border: none !important;
                overflow: visible !important;
                visibility: visible !important;
                z-index: 99999 !important;
            }

            .offcanvas-body {
                display: block !important;
                overflow: visible !important;
                height: auto !important;
                padding: 0 !important;
            }

            /* Multi-column grid spacing for cards */
            .qr-print-grid {
                display: grid !important;
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 20px !important;
                padding: 20px !important;
                background: #ffffff !important;
            }

            .qr-print-card {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                border: 1px solid #b5b5b5 !important;
                background: #ffffff !important;
                padding: 15px !important;
                page-break-inside: avoid !important;
                border-radius: 6px !important;
                box-shadow: none !important;
            }

            .qr-print-card .modified-apc {
                color: #000000 !important;
            }

            .qr-print-card h5 {
                color: #000000 !important;
                font-size: 15px !important;
                font-weight: bold !important;
            }

            .qr-print-card .text-white-50, 
            .qr-print-card .text-muted {
                color: #444444 !important;
                font-size: 12px !important;
            }

            .qr-wrapper {
                border: none !important;
                padding: 0 !important;
                background: #ffffff !important;
            }

            .qr-wrapper svg {
                width: 125px !important;
                height: 125px !important;
            }
        }
      `}</style>
        </>
    );
}

export default XMembersLight;